"use client";

import React from "react";
import { Box, Stack } from "@mui/material";

export interface MarqueeProps {
  /** Rendered once each; the track repeats them to close the loop. */
  items: React.ReactNode[];
  /** Seconds for one full pass. Longer is slower and easier to read. */
  durationS: number;
  /** Gap between items, in MUI spacing units. */
  gap: number;
  /** How far the fade at each end reaches, as a percentage of the width. */
  fade?: number;
}

/**
 * A strip that travels left to right and never ends.
 *
 * Two things on the buy screen need the same trick — the review cards and the
 * trust points — because both have more to say than fits one line on a phone,
 * and neither is worth the vertical space of stacking. Wrapping was tried for
 * the trust points first: three claims became two ragged lines that read worse
 * than one moving line does.
 *
 * The track holds every item twice and travels exactly half its own width, so
 * the second copy arrives where the first began and the seam never shows. That
 * is why "three points on repeat" renders six nodes.
 *
 * Motion never traps the reader: hovering pauses the track, and a viewer who
 * has asked their system to reduce motion gets a still, swipeable row instead.
 */
export default function Marquee({ items, durationS, gap, fade = 6 }: MarqueeProps) {
  if (items.length === 0) return null;

  const track = [...items, ...items];
  // MUI spacing unit is 8px; the loop offset has to include half a gap or the
  // repeat lands a gap short and the strip visibly stutters once per pass.
  const halfGapPx = (gap * 8) / 2;
  const mask = `linear-gradient(to right, transparent, #000 ${fade}%, #000 ${100 - fade}%, transparent)`;

  return (
    <Box
      sx={{
        overflow: "hidden",
        maskImage: mask,
        WebkitMaskImage: mask,
        "&:hover .marquee-track": { animationPlayState: "paused" },
        "@media (prefers-reduced-motion: reduce)": {
          overflowX: "auto",
          maskImage: "none",
          WebkitMaskImage: "none",
        },
      }}
    >
      <Stack
        className="marquee-track"
        direction="row"
        spacing={gap}
        alignItems="center"
        sx={{
          width: "max-content",
          animation: `canvas-marquee ${durationS}s linear infinite`,
          "@keyframes canvas-marquee": {
            from: { transform: `translateX(calc(-50% - ${halfGapPx}px))` },
            to: { transform: "translateX(0)" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        {track.map((item, i) => (
          <Box key={i} sx={{ flex: "0 0 auto" }}>
            {item}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
